// What the GRADER does about a language's own letters — the third of the app's three per-language
// typing tables, and the one that says what is CORRECT rather than what is shown:
//
//   type_subst.js   — the substitutions a learner is TOLD they may type (display)
//   answer_forms.js — the tolerance the REVERSE direction allows on a typed meaning
//   grader_policy.js (here) — what the FORWARD direction accepts as a spelling of the word itself
//
// Every rule here only ever makes the grader STRICTER, and the default is what the app has always
// done, so a language with no row grades exactly as it did before this file existed. A language
// becomes strict by saying so; it never becomes strict by accident, and it never becomes lenient
// here at all.
var GraderPolicy = (function () {
	"use strict";

	// Whether the server's search keys (TermDto.termSearch / termSearchFold) may be handed back to
	// the learner as accepted SPELLINGS of the word.
	//
	// They are keys for the dictionary INDEX first, and that index folds on purpose: Spanish routes
	// every query through DiacriticCharsReplacer so that typing "nino" finds "niño"
	// (DefaultSearchSupport.analyzeQuery). For a language with no translit convention of its own
	// there is no TRANSLIT key, so LeanProjection.termDto falls back and puts that FOLDED string in
	// BOTH TermDto slots. Offered back as accepted spellings they grade "hablo" right for "habló"
	// and "nino" right for "niño" — a present for a preterite, and one noun for a different one.
	//
	// ⛔ The two normalizations are different functions and the whole rule is that they stay
	// different: the search index folds, the grader never does (es-term-onboarding §2.6 rule P6;
	// corpus/py/packs/es.py SEARCH_FOLDS vs GRADER_FOLDS, the second deliberately empty).
	//
	// German is deliberately NOT in the table. Its keys are a real spelling convention a German
	// keyboard forces on people (ä→ae), its base-letter key is already gated on the server by
	// learn.search.german.single-letter-substitution, and training_session.js derives "Aerger" from
	// the term itself in any case. English and Russian fold nothing that distinguishes two words.
	//
	// Pinned from the Java side by SearchKeySpellingPolicyTest, in both directions: every language
	// whose search support folds diacritics must appear here as false, and nothing else may.
	// ES is the language this was built for and the only one of the four with a term corpus today;
	// FR, IT and PT are constructed from the same replacer set on the server, so they are listed
	// now rather than left for whoever gives one of them a corpus to discover the hard way.
	var SEARCH_KEYS_ARE_SPELLINGS = {
		ES: false,
		FR: false,
		IT: false,
		PT: false
	};

	function searchKeysAreSpellings(langCode) {
		var allowed = SEARCH_KEYS_ARE_SPELLINGS[String(langCode || "").toUpperCase()];
		return allowed === undefined ? true : allowed;
	}

	// Which articles a CARD FACE accepts — rule F9's other half, and the only rule in this file
	// that makes the grader more lenient rather than stricter. It is here anyway, and deliberately:
	// the face and the spellings accepted for it are one decision, and the reason F2b session 3
	// found a defect at all is that half of that decision was living somewhere nobody named.
	//
	// ⛔ `el/la` is ONE face and BOTH halves are right. Spanish gender `c` (común — one word, two
	// referents: `el/la estudiante`) and gender `a` (ambiguo — one referent, either gender:
	// `el/la mar`) are different grammar and the sense layer keeps them apart for other reasons,
	// but at the drill surface they impose the same obligation: the learner may say either. So the
	// face itself is accepted, and so is each half on its own.
	//
	// ⚠️ The rule reads the FACE, not a language and not a gender letter. A face with no slash
	// expands to itself, so German's `der` is one string in and one string out — and a language
	// that grows a shared face later is covered without editing anything here.
	function articleAlternatives(article) {
		if (!article || article.indexOf("/") < 0) {
			return article ? [article] : [];
		}
		var out = [article], parts = article.split("/");
		for (var i = 0; i < parts.length; i++) {
			var part = parts[i].trim();
			if (part && out.indexOf(part) < 0) { out.push(part); }
		}
		return out;
	}

	return {
		searchKeysAreSpellings: searchKeysAreSpellings,
		articleAlternatives: articleAlternatives,
		SEARCH_KEYS_ARE_SPELLINGS: SEARCH_KEYS_ARE_SPELLINGS
	};
})();
